import { MonedaService } from '../../services/moneda-service.js';
import { PAISES_DISPONIBLES } from '@shared/config/datos-fiscales-por-pais.js';
import { TabbedModal } from '../tabbed-modal.js';
import { t } from '../../services/i18n-admin.js';
import { icon } from '../../utils/icon.js';

/**
 * Modal "Editar/Nuevo Nodo" — organizado en 6 secciones visuales con el mismo
 * DOM y mismos IDs que el diseño anterior. platform-nodos.js sigue
 * funcionando sin cambios. Este archivo solo aporta estructura visual.
 *
 * Orden intencional:
 *   1. Identidad           → qué es y cómo se llama
 *   2. Localización        → país, idioma, moneda (ADN del nodo)
 *   3. Ubicación física    → solo PV: modalidad, dirección, geo, autorización
 *   4. Operación           → wallet, juegos, series
 *   5. Finanzas y legales  → fiscal, bancario, pasarela
 *   6. Canales y marca     → whatsapp, branding
 *   7. Gateway             → solo integrador_juegos
 *
 * Cada sección es un `<fieldset>` con leyenda; la visibilidad por tipo de
 * nodo la maneja platform-nodos.js.onTipoChange usando los IDs existentes.
 */

// Las secciones se renderizan como paneles planos — el layout de tabs lo
// maneja TabbedModal. Cada render*() devuelve solo el HTML interno.

// ─── 1. Identidad ────────────────────────────────────────────────────────
function renderIdentidad() {
    return `
        <div class="form-group">
          <label class="form-label">${t('Tipo *')}</label>
          <select class="form-select" id="nodoTipo" required data-action="nodos:onTipoChange">
            <option value="">${t('Seleccionar...')}</option>
            <option value="operador">${t('Operador')}</option>
            <option value="representante">${t('Representante')}</option>
            <option value="punto_venta">${t('Punto de Venta')}</option>
            <option value="integrador_juegos">${t('Integrador Juegos')}</option>
          </select>
        </div>

        <div class="form-group">
          <label class="form-label">${t('Nombre *')}</label>
          <input type="text" class="form-input" id="nodoNombre" placeholder="${t('Nombre del nodo')}" required>
        </div>

        <div class="form-group" id="nodoCodigoGroup">
          <label class="form-label">${t('Código (opcional)')}</label>
          <input type="text" class="form-input" id="nodoCodigo" placeholder="${t('Ej: OP-LIMA, PV-001')}">
          <div class="form-hint">${t('Atajo para localizar este nodo en búsquedas.')}</div>
        </div>

        <div class="form-group">
          <label class="form-label">${t('Contacto')}</label>
          <input type="text" class="form-input" id="nodoContacto" placeholder="${t('Email o teléfono')}">
          <label class="form-label" style="margin-top:10px">${t('Teléfono')}</label>
          <input type="tel" class="form-input" id="nodoTelefonoContacto" maxlength="30" placeholder="${t('Teléfono de contacto (con código de país)')}">
        </div>
    `;
}

// ─── 2. Localización (ADN del nodo) ──────────────────────────────────────
function renderLocalizacion() {
    return `
        <div class="form-group">
          <label class="form-label">${t('País')}</label>
          <select class="form-input" id="nodoPais" data-action="nodos:onPaisChange">
            <option value="">${t('Heredar del padre')}</option>
            ${PAISES_DISPONIBLES.map(p => `<option value="${p.codigo}">${p.nombre}</option>`).join('')}
            <option value="OTRO">${t('Otro...')}</option>
          </select>
          <div class="form-hint">${t('Define documentos fiscales, bancos y régimen tributario. Vacío = hereda del ancestro.')}</div>
        </div>

        <div class="form-group">
          <label class="form-label">${t('Idioma del sistema')}</label>
          <select class="form-input" id="nodoIdioma">
            <option value="">${t('Heredar')}</option>
            <option value="es">Español</option>
            <option value="en">English</option>
            <option value="pt">Português</option>
            <option value="ru">Русский</option>
          </select>
          <div class="form-hint">${t('Idioma para mensajes, interfaz de caja y sorteos. Vacío = hereda del ancestro más cercano.')}</div>
        </div>

        <div class="form-group">
          <label class="form-label">Moneda</label>
          <select class="form-input" id="nodoMoneda">
            ${MonedaService.buildSelectorOptions({ incluirHeredar: true })}
          </select>
          <div class="form-hint">${t('Moneda para formateo de montos y estadísticas. Vacío = hereda del ancestro más cercano.')}</div>
        </div>
    `;
}

// ─── 3. Ubicación física (solo PV) ───────────────────────────────────────
// Contiene: modalidad, dirección, accordion Ubicación y accordion Autorización.
// platform-nodos.js los oculta/muestra según tipo.
function renderUbicacionFisica() {
    return `
        <div class="form-group" id="nodoModalidadGroup" style="display:none">
          <label class="form-label">${t('Modalidad')}</label>
          <select class="form-select" id="nodoModalidad">
            <option value="fisico">${t('Fisico (Local con direccion)')}</option>
            <option value="ambulante">${t('Ambulante (Vendedores moviles)')}</option>
            <option value="online">${t('Online (Pasarelas de pago)')}</option>
            <option value="sistema">${t('Sistema (Operaciones automaticas)')}</option>
          </select>
        </div>

        <div class="form-group">
          <label class="form-label">${t('Dirección')}</label>
          <input type="text" class="form-input" id="nodoDireccion" placeholder="${t('Dirección física')}">
        </div>

        <div class="config-accordion" id="nodoUbicacionAccordion" style="display:none;">
          <div class="config-accordion-header" data-action="nodos:toggleAccordion" data-param="nodoUbicacionAccordion">
            <svg class="config-accordion-arrow" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M9 18l6-6-6-6"/></svg>
            <span class="config-accordion-title">${t('Ubicación y Horario')}</span>
            <span class="config-accordion-badge" id="nodoUbicacionBadge">${t('sin datos')}</span>
          </div>
          <div class="config-accordion-body">
            <div class="form-hint" style="margin-bottom:10px">${t('Datos operativos del punto de venta físico.')}</div>
            <div class="form-group">
              <label class="form-label">${t('Departamento / Estado / Región')}</label>
              <input type="text" class="form-input" id="ubicacionRegion" placeholder="${t('Ej: AMAZONAS - UTCUBAMBA - BAGUA GRANDE')}">
            </div>
            <div style="display:flex; gap:8px;">
              <div class="form-group" style="flex:1;">
                <label class="form-label">${t('Latitud')}</label>
                <input type="number" class="form-input" id="ubicacionLatitud" step="any" placeholder="-5.756019">
              </div>
              <div class="form-group" style="flex:1;">
                <label class="form-label">${t('Longitud')}</label>
                <input type="number" class="form-input" id="ubicacionLongitud" step="any" placeholder="-78.438872">
              </div>
            </div>
            <div style="display:flex; gap:8px;">
              <div class="form-group" style="flex:1;">
                <label class="form-label">${t('Horario de Operación')}</label>
                <input type="text" class="form-input" id="ubicacionHorario" placeholder="${t('07:00 - 23:00')}">
              </div>
              <div class="form-group" style="flex:1;">
                <label class="form-label">${t('Días de Operación')}</label>
                <input type="text" class="form-input" id="ubicacionDias" placeholder="${t('Lunes a Domingo')}">
              </div>
            </div>
            <div class="form-group">
              <label class="form-label">${t('Teléfono del Local')}</label>
              <input type="text" class="form-input" id="ubicacionTelefono" placeholder="">
            </div>
            <div class="form-group">
              <label class="form-label">${t('Email del Local')}</label>
              <input type="email" class="form-input" id="ubicacionEmail" placeholder="">
            </div>
          </div>
        </div>

        <div class="config-accordion" id="nodoAutorizacionAccordion" style="display:none;">
          <div class="config-accordion-header" data-action="nodos:toggleAccordion" data-param="nodoAutorizacionAccordion">
            <svg class="config-accordion-arrow" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M9 18l6-6-6-6"/></svg>
            <span class="config-accordion-title" id="autorizacionTitulo">${t('Autorización / Licencia')}</span>
            <span class="config-accordion-badge" id="nodoAutorizacionBadge">${t('sin datos')}</span>
          </div>
          <div class="config-accordion-body">
            <div class="form-hint" style="margin-bottom:10px">${t('Documento legal que se expone en el local. El sistema alertará cuando esté por vencer.')}</div>
            <div class="form-group">
              <label class="form-label">${t('Código / Número de Autorización')}</label>
              <input type="text" class="form-input" id="autorizacionCodigo" placeholder="">
            </div>
            <div class="form-group">
              <label class="form-label">${t('Estado')}</label>
              <select class="form-select" id="autorizacionEstado">
                <option value="">${t('Sin información')}</option>
                <option value="vigente">${t('Vigente')}</option>
                <option value="en_tramite">${t('En Trámite')}</option>
                <option value="vencida">${t('Vencida')}</option>
              </select>
            </div>
            <div class="form-group">
              <label class="form-label">${t('Fecha de Emisión')}</label>
              <input type="date" class="form-input" id="autorizacionFechaEmision">
            </div>
            <div class="form-group">
              <label class="form-label">${t('Fecha de Vencimiento')}</label>
              <input type="date" class="form-input" id="autorizacionFechaVencimiento">
              <div class="form-hint">${t('Se generará una alerta operativa 30 días antes del vencimiento.')}</div>
            </div>
          </div>
        </div>
    `;
}

// ─── 4. Operación (capacidades comerciales) ──────────────────────────────
function renderOperacion() {
    return `
        <div class="config-accordion" id="nodoWalletAccordion">
          <div class="config-accordion-header" data-action="nodos:toggleAccordion" data-param="nodoWalletAccordion">
            <svg class="config-accordion-arrow" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M9 18l6-6-6-6"/></svg>
            <span class="config-accordion-title">${t('Wallet (Recargas)')}</span>
            <span class="config-accordion-badge" id="nodoWalletBadge">${t('sin config')}</span>
          </div>
          <div class="config-accordion-body">
            <div class="form-hint" style="margin-bottom:10px">${t('Se hereda a nodos hijos. Dejar vacío para heredar del padre.')}</div>
            <div class="form-group">
              <label class="form-label">${t('Denominaciones de recarga (en centavos)')}</label>
              <input type="text" class="form-input" id="walletDenominaciones" placeholder="${t('50, 100, 500, 1000, 2000, 5000')}">
              <div class="form-hint">${t('Enteros en la unidad mínima de la moneda (centavos). Ej: 50 = R$ 0,50 / 100 = R$ 1,00 / 5000 = R$ 50,00. Para CLP/COP coincide con el valor entero (50 = $50).')}</div>
            </div>
            <div class="form-group">
              <label class="form-label">${t('Modo de entrada')}</label>
              <select class="form-select" id="walletModoEntrada">
                <option value="">${t('Heredar del padre')}</option>
                <option value="denominacion">${t('Denominación (botones de monto)')}</option>
                <option value="calculadora">${t('Calculadora (numpad)')}</option>
              </select>
            </div>
            <div class="form-group">
              <label class="form-label">${t('Tipo de identificador del cliente')}</label>
              <select class="form-select" id="walletTipoIdentificador" data-action="nodos:onTipoIdentificadorChange">
                <option value="">${t('Heredar del padre')}</option>
                <option value="telefono">${t('Teléfono celular')}</option>
                <option value="email">${t('Correo electrónico')}</option>
                <option value="documento">${t('Documento de identidad (DNI/CE)')}</option>
                <option value="codigo_interno">${t('Código interno del operador')}</option>
              </select>
              <div class="form-hint">${t('Cómo se identifica al cliente en la caja. Default: teléfono.')}</div>
            </div>
            <div class="form-group">
              <label class="form-label">${t('Acciones después de recarga')}</label>
              <div style="display:flex; flex-direction:column; gap:8px; margin-top:4px;">
                <label style="display:flex; align-items:center; gap:6px; cursor:pointer;">
                  <input type="checkbox" id="widgetWhatsapp" checked> ${t('Enviar por WhatsApp')}
                </label>
                <label style="display:flex; align-items:center; gap:6px; cursor:pointer;">
                  <input type="checkbox" id="widgetLinkJuego" checked> ${t('Link de juego')}
                </label>
                <label style="display:flex; align-items:center; gap:6px; cursor:pointer;">
                  <input type="checkbox" id="widgetReferidos" checked> ${t('Referidos (cliente nuevo)')}
                </label>
              </div>
              <div class="form-hint">${t('Widgets que aparecen tras un depósito exitoso.')}</div>
            </div>
          </div>
        </div>

        <div class="config-accordion" id="nodoJuegosAccordion" style="display:none;">
          <div class="config-accordion-header" data-action="nodos:toggleAccordion" data-param="nodoJuegosAccordion">
            <svg class="config-accordion-arrow" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M9 18l6-6-6-6"/></svg>
            <span class="config-accordion-title">${t('Juegos y Restricciones')}</span>
            <span class="config-accordion-badge" id="nodoJuegosBadge">${t('sin config')}</span>
          </div>
          <div class="config-accordion-body">
            <div id="nodoJuegosRestriccionesContainer"></div>
          </div>
        </div>

        <div id="nodoSeriesTicketsSection" style="display:none;">
          <hr style="margin: 16px 0; border-color: var(--border-color);">
          <label class="form-label" style="margin-bottom:8px;">${t('Series de tickets por canal')}</label>
          <div class="form-hint" style="margin-bottom:10px;">${t('Dejá en "Heredar" para usar la serie default global')}</div>
          <div class="form-row">
            <div class="form-group">
              <label class="form-label">${t('Default')}</label>
              <select class="form-input" id="nodoSerieDefault"><option value="">${t('Heredar')}</option></select>
            </div>
            <div class="form-group">
              <label class="form-label">${t('Bearer')}</label>
              <select class="form-input" id="nodoSerieBearer"><option value="">${t('Heredar')}</option></select>
            </div>
          </div>
          <div class="form-row">
            <div class="form-group">
              <label class="form-label">${t('Cuenta')}</label>
              <select class="form-input" id="nodoSerieCuenta"><option value="">${t('Heredar')}</option></select>
            </div>
            <div class="form-group">
              <label class="form-label">${t('VLT')}</label>
              <select class="form-input" id="nodoSerieVlt"><option value="">${t('Heredar')}</option></select>
            </div>
          </div>
          <div style="margin-top:14px;">
            <label class="form-label" style="margin-bottom:6px;">${t('Partners externos')}</label>
            <div class="form-hint" style="margin-bottom:8px;">${t('Opcional: asigná una serie específica a un integrador externo (identificado por su partner_code). Formato: letras, números, guiones o underscores — sin espacios.')}</div>
            <div id="nodoSeriePartnersList"></div>
            <button type="button" class="btn btn-secondary btn-sm" data-action="nodos:agregarPartnerOverrideSerieEnNodo" style="margin-top:6px;">
              ${t('+ Agregar partner')}
            </button>
          </div>
        </div>
    `;
}

// ─── 5. Finanzas y legales ───────────────────────────────────────────────
function renderFinanzas() {
    return `
        <div class="config-accordion" id="nodoFiscalAccordion">
          <div class="config-accordion-header" data-action="nodos:toggleAccordion" data-param="nodoFiscalAccordion">
            <svg class="config-accordion-arrow" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M9 18l6-6-6-6"/></svg>
            <span class="config-accordion-title">${t('Datos Fiscales')}</span>
            <span class="config-accordion-badge" id="nodoFiscalBadge">${t('sin datos')}</span>
          </div>
          <div class="config-accordion-body">
            <div class="form-hint" style="margin-bottom:10px">${t('Identificación tributaria del titular. Las opciones se ajustan al país seleccionado.')}</div>
            <div class="form-group">
              <label class="form-label">${t('Razón Social / Nombre Fiscal')}</label>
              <input type="text" class="form-input" id="fiscalRazonSocial" placeholder="${t('Razón social del titular')}">
            </div>
            <div class="form-group">
              <label class="form-label" id="fiscalTipoDocLabel">${t('Tipo de Documento Fiscal')}</label>
              <select class="form-select" id="fiscalTipoDocumento">
                <option value="">${t('Seleccionar...')}</option>
              </select>
            </div>
            <div class="form-group">
              <label class="form-label" id="fiscalNumDocLabel">${t('Número de Documento')}</label>
              <input type="text" class="form-input" id="fiscalNumeroDocumento" placeholder="">
            </div>
            <div class="form-group">
              <label class="form-label">${t('Dirección Fiscal')}</label>
              <input type="text" class="form-input" id="fiscalDireccionFiscal" placeholder="${t('Dirección legal / sede tributaria')}">
            </div>
            <div class="form-group">
              <label class="form-label" id="fiscalRegimenLabel">${t('Régimen Tributario')}</label>
              <select class="form-select" id="fiscalRegimen">
                <option value="">${t('Seleccionar...')}</option>
              </select>
            </div>
          </div>
        </div>

        <div class="config-accordion" id="nodoBancarioAccordion">
          <div class="config-accordion-header" data-action="nodos:toggleAccordion" data-param="nodoBancarioAccordion">
            <svg class="config-accordion-arrow" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M9 18l6-6-6-6"/></svg>
            <span class="config-accordion-title">${t('Datos Bancarios')}</span>
            <span class="config-accordion-badge" id="nodoBancarioBadge">${t('sin datos')}</span>
          </div>
          <div class="config-accordion-body">
            <div class="form-hint" style="margin-bottom:10px">${t('Cuenta para liquidaciones y pagos. Las opciones se ajustan al país seleccionado.')}</div>
            <div class="form-group">
              <label class="form-label">${t('Billetera digital')} <span class="form-hint" style="display:inline">(${t('Chave PIX en Brasil · Yape/Plin en Perú')})</span></label>
              <input type="text" class="form-input" id="bancarioBilleteraDigital" maxlength="120" placeholder="${t('Clave/chave o celular de la billetera')}" style="margin-bottom:10px">
              <label class="form-label" id="bancarioBancoLabel">${t('Banco')}</label>
              <select class="form-select" id="bancarioBanco">
                <option value="">${t('Seleccionar...')}</option>
              </select>
            </div>
            <div class="form-group">
              <label class="form-label">${t('Tipo de Cuenta')}</label>
              <select class="form-select" id="bancarioTipoCuenta">
                <option value="">${t('Seleccionar...')}</option>
              </select>
            </div>
            <div class="form-group">
              <label class="form-label">${t('Número de Cuenta')}</label>
              <input type="text" class="form-input" id="bancarioNumeroCuenta" placeholder="">
            </div>
            <div class="form-group">
              <label class="form-label" id="bancarioInterbancarioLabel">${t('Cuenta Interbancaria')}</label>
              <input type="text" class="form-input" id="bancarioInterbancario" placeholder="">
            </div>
            <div class="form-group">
              <label class="form-label">${t('Titular de la Cuenta')}</label>
              <input type="text" class="form-input" id="bancarioTitular" placeholder="${t('Nombre del titular (si difiere de la razón social)')}">
            </div>
          </div>
        </div>

        <div id="techoComisionPromotoresWrap" style="display:none;margin-bottom:12px;padding:10px;border:1px solid var(--border);border-radius:8px">
            <label class="form-label">${t('Techo de comisión para promotores')} <span class="form-hint" style="display:inline">(${t('doble seguridad: nadie en su red puede dar más que esto a un promotor — vacío = sin techo')})</span></label>
            <input type="number" class="form-input" id="techoComisionPromotores" min="0" max="100" step="0.5" placeholder="Ej: 12">
        </div>

        <div class="config-accordion" id="nodoPasarelaAccordion">
          <div class="config-accordion-header" data-action="nodos:toggleAccordion" data-param="nodoPasarelaAccordion">
            <svg class="config-accordion-arrow" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M9 18l6-6-6-6"/></svg>
            <span class="config-accordion-title">${t('Pasarela de Pagos')}</span>
            <span class="config-accordion-badge" id="nodoPasarelaBadge">${t('sin config')}</span>
          </div>
          <div class="config-accordion-body">
            <div class="form-hint" style="margin-bottom:8px">${t('Pagos digitales (PIX, etc.). Se hereda a nodos hijos.')}</div>
            <span class="form-hint" id="nodoPasarelaOrigenLabel" style="display:none;"></span>

            <div id="nodoPasarelaProvidersList"></div>
            <button type="button" class="btn btn-sm" id="nodoPasarelaAddProvider" data-action="nodos:addPasarelaProvider" style="margin-top:8px;">
              + Agregar proveedor
            </button>

            <div style="margin:16px 0 4px; font-weight:500; font-size:12px; color:var(--text-secondary);">${t('Límites globales')}</div>
            <div class="form-row">
              <div class="form-group" style="flex:1">
                <label class="form-label">${t('Recarga mín (centavos)')}</label>
                <input type="number" id="nodoPasarelaRecargaMin" class="form-input" placeholder="200">
              </div>
              <div class="form-group" style="flex:1">
                <label class="form-label">${t('Recarga máx (centavos)')}</label>
                <input type="number" id="nodoPasarelaRecargaMax" class="form-input" placeholder="5000000">
              </div>
            </div>
            <div class="form-row">
              <div class="form-group" style="flex:1">
                <label class="form-label">${t('Retiro mín (centavos)')}</label>
                <input type="number" id="nodoPasarelaRetiroMin" class="form-input" placeholder="500">
              </div>
              <div class="form-group" style="flex:1">
                <label class="form-label">${t('Retiro máx / transacción')}</label>
                <input type="number" id="nodoPasarelaRetiroMaxTransaccion" class="form-input" placeholder="100000">
              </div>
            </div>
            <div class="form-row">
              <div class="form-group" style="flex:1">
                <label class="form-label">${t('Retiro máx diario / cliente')}</label>
                <input type="number" id="nodoPasarelaRetiroMaxDiario" class="form-input" placeholder="100000">
              </div>
              <div class="form-group" style="flex:1; display:flex; align-items:center; gap:6px; padding-top:18px;">
                <input type="checkbox" id="nodoPasarelaRetiroInmediato" checked>
                <label for="nodoPasarelaRetiroInmediato" class="form-label" style="margin:0">${t('Retiro inmediato')}</label>
              </div>
            </div>

            <div style="margin:12px 0 4px; font-weight:500; font-size:12px; color:var(--text-secondary);">${t('Campos requeridos al jugador')}</div>
            <div class="form-row" style="gap:16px;">
              <div>
                <div style="font-size:11px; color:var(--text-muted); margin-bottom:4px;">${t('En recarga:')}</div>
                <label style="display:block; font-size:12px;"><input type="checkbox" class="nodoPasarelaReqRecarga" value="nombre"> ${t('Nombre')}</label>
                <label style="display:block; font-size:12px;"><input type="checkbox" class="nodoPasarelaReqRecarga" value="documento"> ${t('Documento')}</label>
                <label style="display:block; font-size:12px;"><input type="checkbox" class="nodoPasarelaReqRecarga" value="email"> ${t('Email')}</label>
                <label style="display:block; font-size:12px;"><input type="checkbox" class="nodoPasarelaReqRecarga" value="telefono"> ${t('Teléfono')}</label>
              </div>
              <div>
                <div style="font-size:11px; color:var(--text-muted); margin-bottom:4px;">${t('En retiro:')}</div>
                <label style="display:block; font-size:12px;"><input type="checkbox" class="nodoPasarelaReqRetiro" value="nombre"> ${t('Nombre')}</label>
                <label style="display:block; font-size:12px;"><input type="checkbox" class="nodoPasarelaReqRetiro" value="documento"> ${t('Documento')}</label>
                <label style="display:block; font-size:12px;"><input type="checkbox" class="nodoPasarelaReqRetiro" value="email"> ${t('Email')}</label>
                <label style="display:block; font-size:12px;"><input type="checkbox" class="nodoPasarelaReqRetiro" value="telefono"> ${t('Teléfono')}</label>
              </div>
            </div>
          </div>
        </div>
    `;
}

// ─── 6. Canales y marca ──────────────────────────────────────────────────
function renderCanalesYMarca() {
    return `
        <div class="form-group">
          <label class="form-label">
            <input type="checkbox" id="nodoWhatsappHabilitado" style="margin-right:6px; vertical-align:middle;">
            ${t('Canal WhatsApp habilitado')}
          </label>
          <div class="form-hint" style="margin-bottom:8px;">${t('Si está habilitado, el POS podrá enviar el ticket al WhatsApp del cliente (motivo de reimpresión o venta al wallet).')}</div>
          <input type="tel" class="form-input" id="nodoWhatsappTelefono" placeholder="${t('999888777 (opcional — número del comercio)')}">
          <div class="form-hint">${t('Número del comercio (sin prefijo de país). Opcional: si está vacío, el POS pedirá el número al cliente al enviar.')}</div>
        </div>

        <div class="config-accordion" id="nodoBrandingAccordion">
          <div class="config-accordion-header" data-action="nodos:toggleAccordion" data-param="nodoBrandingAccordion">
            <svg class="config-accordion-arrow" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M9 18l6-6-6-6"/></svg>
            <span class="config-accordion-title">${t('Branding')}</span>
            <span class="config-accordion-badge" id="nodoBrandingBadge">${t('sin config')}</span>
          </div>
          <div class="config-accordion-body">
            <div class="form-hint" style="margin-bottom:6px">${t('Logos personalizados del operador. Se heredan a nodos hijos.')}</div>
            <div style="margin-bottom:8px; font-size:10px; color:var(--text-muted);">${t('PNG, SVG o WebP con transparencia — max 2MB')}</div>
            <div style="display:flex; gap:12px; flex-wrap:wrap;">
              <div style="flex:1; min-width:140px; text-align:center;">
                <label class="form-label" style="font-size:11px;">${t('Logo Splash')}</label>
                <div style="height:72px; display:flex; align-items:center; justify-content:center;">
                  <img id="nodoBrandingPreviewSplash" src="" alt="" style="max-width:90px; height:64px; display:none; border-radius:6px; background:#f0f0f3; padding:4px; object-fit:contain;">
                  <span id="nodoBrandingLabelSplash" style="font-size:11px; color:var(--text-muted);">${t('Sin imagen')}</span>
                </div>
                <div style="margin-top:4px; display:flex; gap:4px; justify-content:center;">
                  <button type="button" class="btn btn-sm" data-action="nodos:uploadBranding" data-param="logoSplash" style="font-size:11px; padding:2px 8px;">${t('Cambiar')}</button>
                  <button type="button" class="btn btn-sm btn-danger" data-action="nodos:resetBranding" data-param="logoSplash" style="font-size:11px; padding:2px 8px;">${t('Reset')}</button>
                </div>
                <input type="file" id="nodoBrandingFileSplash" accept=".png,.svg,.webp" style="display:none;" data-action="nodos:onBrandingFileSelected" data-param="logoSplash">
              </div>
              <div style="flex:1; min-width:140px; text-align:center;">
                <label class="form-label" style="font-size:11px;">${t('Logo Login')}</label>
                <div style="height:72px; display:flex; align-items:center; justify-content:center;">
                  <img id="nodoBrandingPreviewLogin" src="" alt="" style="max-width:90px; height:64px; display:none; border-radius:6px; background:#f0f0f3; padding:4px; object-fit:contain;">
                  <span id="nodoBrandingLabelLogin" style="font-size:11px; color:var(--text-muted);">${t('Sin imagen')}</span>
                </div>
                <div style="margin-top:4px; display:flex; gap:4px; justify-content:center;">
                  <button type="button" class="btn btn-sm" data-action="nodos:uploadBranding" data-param="logoLogin" style="font-size:11px; padding:2px 8px;">${t('Cambiar')}</button>
                  <button type="button" class="btn btn-sm btn-danger" data-action="nodos:resetBranding" data-param="logoLogin" style="font-size:11px; padding:2px 8px;">${t('Reset')}</button>
                </div>
                <input type="file" id="nodoBrandingFileLogin" accept=".png,.svg,.webp" style="display:none;" data-action="nodos:onBrandingFileSelected" data-param="logoLogin">
              </div>
              <div style="flex:1; min-width:140px; text-align:center;">
                <label class="form-label" style="font-size:11px;">${t('Logo Ticket')}</label>
                <div style="height:72px; display:flex; align-items:center; justify-content:center;">
                  <img id="nodoBrandingPreviewTicket" src="" alt="" style="max-width:90px; height:64px; display:none; border-radius:6px; background:#f0f0f3; padding:4px; object-fit:contain;">
                  <span id="nodoBrandingLabelTicket" style="font-size:11px; color:var(--text-muted);">${t('Sin imagen')}</span>
                </div>
                <div style="margin-top:4px; display:flex; gap:4px; justify-content:center;">
                  <button type="button" class="btn btn-sm" data-action="nodos:uploadBranding" data-param="logoTicket" style="font-size:11px; padding:2px 8px;">${t('Cambiar')}</button>
                  <button type="button" class="btn btn-sm btn-danger" data-action="nodos:resetBranding" data-param="logoTicket" style="font-size:11px; padding:2px 8px;">${t('Reset')}</button>
                </div>
                <input type="file" id="nodoBrandingFileTicket" accept=".png,.svg,.webp" style="display:none;" data-action="nodos:onBrandingFileSelected" data-param="logoTicket">
              </div>
            </div>
            <div style="margin-top:12px; padding-top:8px; border-top:1px solid var(--border);">
              <label class="form-label" style="font-size:11px;">${t('Efecto Shine (Splash y Login)')}</label>
              <div style="display:flex; gap:12px; align-items:center; margin-top:4px;">
                <label style="font-size:11px; color:var(--text-muted);">${t('Color 1')}</label>
                <input type="color" id="nodoBrandingShineColor1" value="#ffd700" style="width:32px;height:24px;border:none;cursor:pointer;" data-action="nodos:onShineColorChange">
                <label style="font-size:11px; color:var(--text-muted);">${t('Color 2')}</label>
                <input type="color" id="nodoBrandingShineColor2" value="#ffffff" style="width:32px;height:24px;border:none;cursor:pointer;" data-action="nodos:onShineColorChange">
              </div>
              <div class="form-hint" style="margin-top:4px;">${t('Solo aplica a logos SVG. PNG/WebP se muestran sin efecto.')}</div>
            </div>
          </div>
        </div>

        <div class="config-accordion" id="nodoMarketingAccordion">
          <div class="config-accordion-header" data-action="nodos:toggleAccordion" data-param="nodoMarketingAccordion">
            <svg class="config-accordion-arrow" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M9 18l6-6-6-6"/></svg>
            <span class="config-accordion-title">${t('Píxel de Meta / GTM')}</span>
            <span class="config-accordion-badge" id="nodoMarketingBadge">${t('sin config')}</span>
          </div>
          <div class="config-accordion-body">
            <div class="form-hint" style="margin-bottom:8px">${t('Píxel de seguimiento del operador para optimizar su pauta paga en Meta/Google. Se hereda a los nodos hijos (un Promotor Digital sin píxel propio usa el de su operador). Solo se inyecta a visitantes que vienen de una campaña.')}</div>
            <div class="form-group">
              <label class="form-label">${t('Píxel de Meta (ID)')}</label>
              <input type="text" class="form-input" id="nodoMetaPixelId" inputmode="numeric" placeholder="${t('123456789012345')}">
              <div class="form-hint">${t('Solo dígitos (15-16). ID de Facebook/Meta Pixel.')}</div>
            </div>
            <div class="form-group">
              <label class="form-label">${t('GTM Container ID')}</label>
              <input type="text" class="form-input" id="nodoGtmContainerId" placeholder="GTM-XXXXXXX" style="text-transform:uppercase;">
              <div class="form-hint">${t('Formato GTM-XXXXXXX. Google Tag Manager.')}</div>
            </div>
          </div>
        </div>
    `;
}

// ─── 7. POS (Legal + Branding del ticket térmico) ─────────────────────────
//
// Accordion con los únicos campos nuevos del POS. El resto (razón social,
// licencia, teléfono contacto) se reutiliza de sus accordions originales —
// SSOT puro, cero duplicación.
//
// El namespace `ticketLegalPOS.*` + `brandingTicketPOS.*` aísla estos
// campos del branding visual del POS (Splash/Login/Ticket logos) que vive
// en `branding.*`. Distinto propósito, distinto namespace.
function renderPOS() {
    return `
        <div class="config-accordion" id="nodoTicketPosAccordion">
          <div class="config-accordion-header" data-action="nodos:toggleAccordion" data-param="nodoTicketPosAccordion">
            <svg class="config-accordion-arrow" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M9 18l6-6-6-6"/></svg>
            <span class="config-accordion-title">${t('Ticket POS (legal + impresión)')}</span>
            <span class="config-accordion-badge" id="nodoTicketPosBadge">${t('sin config')}</span>
          </div>
          <div class="config-accordion-body">

            <div class="form-hint" style="margin-bottom:10px">
              Estos campos viajan al POS Android y se imprimen en el ticket térmico.
              Razón social, número fiscal, licencia y teléfono se toman de los accordions
              Fiscales/Autorización/Canales (SSOT — no se duplican aquí).
            </div>

            <div class="form-group">
              <label class="form-label">${t('Validez del premio (días)')}</label>
              <input type="number" class="form-input" id="ticketLegalValidezPremioDias"
                     min="1" max="365" placeholder="30"
                     style="max-width:160px">
              <div class="form-hint">${t('Aparece en el texto legal del ticket (ej: "Premio válido 30 días desde el sorteo"). Vacío = heredar (default 30).')}</div>
            </div>

            <div class="form-group">
              <label class="form-label">${t('Texto adicional legal')}</label>
              <textarea class="form-input" id="ticketLegalTextoAdicional"
                        rows="2" placeholder="${t('Ej: Consultas 24h al 0800-xxx-xxxx')}"></textarea>
              <div class="form-hint">${t('Se concatena al final del bloque legal con separador "·". Opcional — vacío = sin texto extra.')}</div>
            </div>

            <div style="border-top:1px solid var(--border); margin:12px 0; padding-top:10px;">
              <div style="font-size:12px; font-weight:600; color:var(--text-secondary); margin-bottom:8px;">${t('Branding del ticket')}</div>
            </div>

            <div class="form-group">
              <label class="form-label">${t('Nombre del operador en el pie')}</label>
              <input type="text" class="form-input" id="brandingTicketOperatorFooterName"
                     placeholder="${t('Ej: Lotería Nacional SAC')}">
              <div class="form-hint">${t('Aparece en el pie del ticket. Vacío = heredar del ancestro.')}</div>
            </div>

            <div class="form-group">
              <label class="form-label">${t('URL base para QR de resultados')}</label>
              <input type="url" class="form-input" id="brandingTicketQrBaseUrl"
                     placeholder="${t('https://resultados.loteriaejemplo.com')}">
              <div class="form-hint">${t('Solo dominio raíz — el POS compone')} <code>{qrBaseUrl}/?tiquete={codigo}</code>${t('. Si hay QR, el Code128 se omite por default (ahorro de papel).')}</div>
            </div>

            <div class="form-group">
              <label class="form-label">${t('Footer extra')}</label>
              <input type="text" class="form-input" id="brandingTicketFooterExtra"
                     placeholder="${t('Ej: Síguenos @loteriax')}">
              <div class="form-hint">${t('Texto decorativo al final del ticket. Opcional.')}</div>
            </div>

            <div class="form-group">
              <label class="form-label">${t('Imprimir Code128 (código de barras)')}</label>
              <select class="form-select" id="brandingTicketImprimirCodeBar" style="max-width:280px">
                <option value="">${t('Heredar (default: si no hay QR, sí; si hay QR, no)')}</option>
                <option value="true">${t('Forzar: sí imprimir')}</option>
                <option value="false">${t('Forzar: no imprimir')}</option>
              </select>
              <div class="form-hint">${t('Regla dura: el ticket nunca sale sin algún código escaneable. Si no hay QR y se elige "no imprimir", se fuerza a sí imprimir.')}</div>
            </div>

          </div>
        </div>
    `;
}

// ─── 8. Gateway (solo integrador_juegos) ─────────────────────────────────
// Visibilidad la maneja platform-nodos.js mostrando/ocultando #nodoGatewayConfig.
function renderGateway() {
    return `
        <div id="nodoGatewayConfig" style="display:none;">
          ${`
            <div class="config-accordion open" id="nodoGatewayIdentityAccordion">
              <div class="config-accordion-header" data-action="nodos:toggleAccordion" data-param="nodoGatewayIdentityAccordion">
                <svg class="config-accordion-arrow" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M9 18l6-6-6-6"/></svg>
                <span class="config-accordion-title">${t('Identidad del Socio')}</span>
                <span class="config-accordion-badge" id="nodoGatewayIdentityBadge">${t('requerido')}</span>
              </div>
              <div class="config-accordion-body">
                <div class="form-group">
                  <label class="form-label">${t('Código del socio *')}</label>
                  <input type="text" class="form-input" id="gatewayPartnerCode"
                         placeholder="${t('Ej: CASINO_VIVO')}" style="text-transform:uppercase">
                  <div class="form-hint">${t('Identificador único. Formato: EMPRESA_PRODUCTO. No cambiar después de tener usuarios.')}</div>
                </div>
                <div class="form-group">
                  <label class="form-label">${t('Protocolo de integración')}</label>
                  <select class="form-input" id="gatewayProtocol">
                    <option value="loto-system">${t('LOTO-SYSTEM (nativo)')}</option>
                    <option value="tigrao">${t('Tigrao Wallet (estándar)')}</option>
                  </select>
                  <div class="form-hint">${t('Tigrao: HMAC con campos ordenados, formato BetWin estándar. Nativo: HMAC sobre body JSON.')}</div>
                </div>
                <div class="form-group">
                  <label class="form-label">${t('URL del Frontend (Game)')}</label>
                  <input type="url" class="form-input" id="gatewayFrontendUrl"
                         placeholder="${import.meta.env.VITE_CUSTOMER_URL || 'https://juega.example.com'}">
                  <div class="form-hint">${t('URL donde el jugador abre el juego. Se usa para construir la gameURL en /userAuth. Dejar vacío para usar la URL por defecto.')}</div>
                </div>
              </div>
            </div>

            <div class="config-accordion open" id="nodoGatewayEndpointsAccordion">
              <div class="config-accordion-header" data-action="nodos:toggleAccordion" data-param="nodoGatewayEndpointsAccordion">
                <svg class="config-accordion-arrow" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M9 18l6-6-6-6"/></svg>
                <span class="config-accordion-title">${t('Endpoints')}</span>
                <span class="config-accordion-badge" id="nodoGatewayEndpointsBadge">${t('requerido')}</span>
              </div>
              <div class="config-accordion-body">
                <div style="font-size:12px; font-weight:600; color:var(--text-secondary); margin-bottom:6px; text-transform:uppercase; letter-spacing:0.5px;">
                  ${t('Del socio (nuestro backend llama a estas URLs)')}
                </div>
                <div class="form-group">
                  <label class="form-label">${t('URL Balance *')}</label>
                  <input type="url" class="form-input" id="gatewayUrlBalance"
                         placeholder="https://api.socio.com/lotowheel/balance">
                  <div class="form-hint">${t('GET — Consulta saldo. Params:')} <code>?userId=xxx</code>${t('. También valida tokens de auth.')}</div>
                </div>
                <div class="form-group">
                  <label class="form-label">${t('URL BetWin *')}</label>
                  <input type="url" class="form-input" id="gatewayUrlBetwin"
                         placeholder="https://api.socio.com/lotowheel/betwin">
                  <div class="form-hint">${t('POST — Apuesta + premio atómico. Body:')} <code>userId, roundId, totalBet, totalWin</code></div>
                </div>
                <div style="font-size:12px; font-weight:600; color:var(--text-secondary); margin:16px 0 6px; text-transform:uppercase; letter-spacing:0.5px;">
                  ${t('Nuestra (el socio redirige sus usuarios aquí)')}
                </div>
                <div class="form-group">
                  <label class="form-label">${t('URL Auth (informativo)')}</label>
                  <input type="text" class="form-input" id="gatewayOurAuthUrl"
                         readonly style="opacity:0.7; cursor:default;">
                  <div class="form-hint">${t('El socio redirige al jugador aquí con')} <code>?token=xxx&deviceId=yyy</code></div>
                </div>
              </div>
            </div>

            <div class="config-accordion open" id="nodoGatewayCredsAccordion">
              <div class="config-accordion-header" data-action="nodos:toggleAccordion" data-param="nodoGatewayCredsAccordion">
                <svg class="config-accordion-arrow" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M9 18l6-6-6-6"/></svg>
                <span class="config-accordion-title">${t('Credenciales')}</span>
                <span class="config-accordion-badge" id="nodoGatewayCredsBadge">${t('requerido')}</span>
              </div>
              <div class="config-accordion-body">
                <div class="form-hint" style="margin-bottom:10px">
                  ${t('Claves para autenticar y firmar las peticiones hacia el socio.')}
                </div>
                <div class="form-group">
                  <label class="form-label">${t('API Key *')}</label>
                  <input type="text" class="form-input" id="gatewayApiKey"
                         placeholder="${t('pk_live_xxx')}">
                  <div class="form-hint">${t('Identifica al integrador. Tigrao: se usa en')} <code>Authorization: Bearer</code>${t(' para gamelist. Nativo: header ')} <code>X-API-Key</code>.</div>
                </div>
                <div class="form-group">
                  <label class="form-label">${t('API Secret (HMAC) *')}</label>
                  <div style="display:flex; gap:4px;">
                    <input type="password" class="form-input" id="gatewayApiSecret"
                           placeholder="${t('sk_live_xxx')}" style="flex:1">
                    <button type="button" class="btn btn-secondary btn-sm"
                            data-action="nodos:toggleSecretVisibility" title="${t('Ver/ocultar')}">
                      ${icon('eye', '', { id: 'gatewaySecretToggleIcon' })}
                    </button>
                  </div>
                  <div class="form-hint">${t('Firma HMAC-SHA256 para integridad y autenticación. Nunca visible en logs.')}</div>
                </div>
              </div>
            </div>

            <div class="config-accordion" id="nodoGatewayAdvancedAccordion">
              <div class="config-accordion-header" data-action="nodos:toggleAccordion" data-param="nodoGatewayAdvancedAccordion">
                <svg class="config-accordion-arrow" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M9 18l6-6-6-6"/></svg>
                <span class="config-accordion-title">${t('Configuración Avanzada')}</span>
                <span class="config-accordion-badge" id="nodoGatewayAdvancedBadge">${t('opcional')}</span>
              </div>
              <div class="config-accordion-body">
                <div class="form-group">
                  <label class="form-label">${t('Operator ID')}</label>
                  <input type="text" class="form-input" id="gatewayOperatorId"
                         placeholder="${t('(usa Código del socio si vacío)')}">
                  <div class="form-hint">${t('ID que se envía como')} <code>operatorId</code>${t(' en el protocolo Tigrao. Si vacío, usa el Código del socio.')}</div>
                </div>
                <div class="form-group">
                  <label class="form-label">${t('Timeout (ms)')}</label>
                  <input type="number" class="form-input" id="gatewayTimeoutMs"
                         placeholder="5000" min="1000" max="30000" step="500">
                  <div class="form-hint">${t('Timeout para llamadas HTTP al socio. Default: 5000ms. Rango: 1000-30000ms.')}</div>
                </div>
              </div>
            </div>
          `}
        </div>
    `;
}

// ─── Modal completo ──────────────────────────────────────────────────────
// ─── Definición de tabs ──────────────────────────────────────────────────
const NODO_TABS = [
    { id: 'identidad',       icon: 'user',       title: t('Identidad'),       desc: t('Nombre y tipo') },
    { id: 'localizacion',    icon: 'landmark',    title: t('Localización'),    desc: t('País, idioma, moneda') },
    { id: 'ubicacion',       icon: 'map-pin',     title: t('Ubicación'),       desc: t('Dirección y horario') },
    { id: 'operacion',       icon: 'settings',    title: t('Operación'),       desc: t('Wallet y juegos') },
    { id: 'finanzas',        icon: 'scale',        title: t('Finanzas'),        desc: t('Fiscal y bancario') },
    { id: 'canales',         icon: 'tag',          title: t('Canales'),         desc: t('WhatsApp y marca') },
    { id: 'ticketPos',       icon: 'receipt',      title: t('Ticket POS'),      desc: t('Legal e impresión') },
];

// ─── SSOT: tabs visibles por PERFIL del nodo ─────────────────────────────
// Cada tipo/variante edita SOLO lo que le aplica — un Promotor Digital no
// tiene local físico, ni imprime tickets, ni configura WhatsApp de venta;
// un operador no tiene dirección de local. El perfil lo deriva el caller
// (tipo + flags de config) y aplicarVisibilidadDeTabsPorPerfil lo pinta.
// Decisión del dueño (2026-06-11): el refinado de perfiles se hace POR PARTES,
// un tipo de nodo a la vez con el cliente. Hoy SOLO promotor_digital filtra;
// el resto de tipos ve la edición completa de siempre. Para refinar el
// siguiente tipo: agregar su fila acá (una línea) — modular y separado.
const TODAS = ['identidad', 'localizacion', 'ubicacion', 'operacion', 'finanzas', 'canales', 'ticketPos'];
const TABS_POR_PERFIL = {
    // Promotor Digital: edita su identidad y sus datos de pago (fiscal/bancario
    // PROPIO — la sección Pasarela se oculta aparte: es del área del operador).
    // Sin local, sin tickets, sin canales; su marketing vive en SU panel.
    promotor_digital: ['identidad', 'finanzas'],
    // PV físico: la edición completa (tiene local, vende tickets, imprime).
    punto_venta: TODAS,
    // Operador/Representante: todo menos Ubicación (dirección/horario DE LOCAL —
    // no tienen local; su domicilio fiscal vive en Finanzas). Plan integral
    // aprobado por el dueño 2026-06-12.
    operador: ['identidad', 'localizacion', 'operacion', 'finanzas', 'canales', 'ticketPos'],
    representante: ['identidad', 'localizacion', 'operacion', 'finanzas', 'canales', 'ticketPos'],
    // Integrador externo: API + dinero (su Gateway propio se muestra aparte);
    // sin local, sin WhatsApp de venta, sin impresión de tickets.
    integrador_externo: ['identidad', 'localizacion', 'operacion', 'finanzas'],
};

/**
 * Deriva el perfil de edición de un nodo (tipo + flags de config).
 * @param {object} nodo  { tipo, config } (config ya parseado o {})
 */
export function derivarPerfilDeEdicion(nodo) {
    const config = (nodo && nodo.config) || {};
    if (config.es_promotor_digital) return 'promotor_digital';
    if (config.is_external) return 'integrador_externo';
    return (nodo && nodo.tipo) || 'punto_venta';
}

/**
 * Oculta el sidebar de tabs mientras el modal hidrata (evita el flash de
 * "todas las tabs" antes de conocer el perfil del nodo). Llamar al ABRIR;
 * aplicarVisibilidadDeTabsPorPerfil lo vuelve a mostrar ya filtrado.
 */
export function ocultarTabsDuranteHidratacion() {
    const sidebar = document.querySelector('#modalNodo .modal-tabs');
    if (sidebar) sidebar.style.visibility = 'hidden';
}

/**
 * Muestra/oculta tabs y panels del modalNodo según el perfil, y deja activo
 * el tab Identidad (siempre visible). Llamar al ABRIR el modal, después de
 * conocer el nodo (en "nuevo" sin tipo aún → mostrar todo).
 */
export function aplicarVisibilidadDeTabsPorPerfil(perfil) {
    const visibles = TABS_POR_PERFIL[perfil] || TABS_POR_PERFIL.punto_venta;
    for (const t of NODO_TABS) {
        const tabBtn = document.querySelector(`#modalNodo .modal-tab[data-tab="${t.id}"]`);
        const panel = document.getElementById(`modalNodo-panel-${t.id}`);
        const mostrar = visibles.includes(t.id);
        if (tabBtn) {
            tabBtn.style.display = mostrar ? '' : 'none';
            tabBtn.classList.toggle('active', mostrar && t.id === 'identidad');
        }
        if (panel && !mostrar) panel.style.display = 'none';
        if (panel && t.id === 'identidad') panel.style.display = 'block';
    }
    const sidebar = document.querySelector('#modalNodo .modal-tabs');
    if (sidebar) sidebar.style.visibility = '';
    // Etiqueta del Tipo: el promotor ES estructuralmente un punto_venta (eso
    // viaja al backend, no cambia), pero el operador debe LEER "Promotor
    // Digital" — la plomería no se muestra. Option espejo con el mismo value:
    // .value sigue devolviendo 'punto_venta' al guardar.
    const selTipo = document.getElementById('nodoTipo');
    if (selTipo) {
        let espejo = document.getElementById('nodoTipoEtiquetaPromotor');
        if (perfil === 'promotor_digital') {
            if (!espejo) {
                espejo = document.createElement('option');
                espejo.id = 'nodoTipoEtiquetaPromotor';
                espejo.value = 'punto_venta';
                espejo.textContent = 'Promotor Digital';
                selTipo.appendChild(espejo);
            }
            espejo.selected = true;
        } else if (espejo) {
            espejo.remove();
        }
    }
    // Secciones DENTRO de un tab que no aplican al perfil. La Pasarela de Pagos
    // es del área del operador (decisión del dueño 2026-06-11): el promotor ve
    // su tab Finanzas solo con SUS datos fiscales/bancarios (para cobrar su
    // comisión), jamás los presets PIX ni límites del operador.
    const pasarela = document.getElementById('nodoPasarelaAccordion');
    if (pasarela) pasarela.style.display = perfil === 'promotor_digital' ? 'none' : '';
}

export function nodoModal() {
    const panels = {
        identidad:    renderIdentidad(),
        localizacion: renderLocalizacion(),
        ubicacion:    renderUbicacionFisica(),
        operacion:    renderOperacion(),
        finanzas:     renderFinanzas(),
        canales:      renderCanalesYMarca(),
        ticketPos:    renderPOS(),
    };

    const tabbedBody = TabbedModal.render({
        tabs: NODO_TABS,
        panels,
        defaultTab: 'identidad',
        modalId: 'modalNodo',
    });

    return `
    <div class="modal" id="modalNodo" style="display:none; max-width:720px;">
      <div class="modal-header">
        <h2 id="modalNodoTitle">${t('Nuevo Nodo')}</h2>
        <button class="btn-icon" data-action="app:closeModal">
          ${icon('x')}
        </button>
      </div>
      <div class="modal-body">
        <form id="formNodo" data-action="nodos:save" style="display:contents;">
          <input type="hidden" id="nodoId">
          <input type="hidden" id="nodoParentId">
          <div id="nodoConfigAccordions" style="display:contents;">
            ${tabbedBody}
          </div>
          ${renderGateway()}
        </form>
      </div>
      <div class="modal-footer">
        <button class="btn btn-secondary" data-action="app:closeModal">${t('Cancelar')}</button>
        <button id="btnGuardarNodo" class="btn btn-primary" data-action="nodos:save">
          ${icon('check')}
          ${t('Guardar')}
        </button>
      </div>
    </div>`;
}
